/**
 * StrategiesPlaybooksContext
 *
 * Provides shared state management for strategies and playbooks across desktop and mobile views.
 * Handles data fetching, selection state, dirty tracking, cross-tab sync subscriptions,
 * and URL hydration (reading URL params on initial load to restore selection).
 *
 * Pattern follows CODING_STANDARDS:
 * - Context for truly global state; thin consumers, all logic in provider
 * - Sync refs during render body (not useEffect) for dirty tracking
 * - Queue updates while dirty, apply on dirty→clean transition
 * - Service singleton imports (strategyService, playbookService, syncCoordinatorService)
 *
 * URL State Architecture:
 * - Hydration (URL → state): Handled in this context via isHydrating gate
 * - Push (state → URL): Handled at page layer via simple effect after hydration
 *
 * UI-only state (collapse states, popovers, modals) remains in page components.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import { strategyService } from '@/services/StrategyService';
import { playbookService } from '@/services/PlaybookService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import { useToastNotification } from '@enfomentis/ui';
import type { Strategy } from '@/types/schema/Strategy';
import type { Playbook, PlaybookConfluenceLink } from '@/types/schema/Playbook';

// =============================================================================
// Helpers
// =============================================================================

/** Sort by status (active first) then alphabetically by name (case-insensitive) */
const sortByStatusThenName = <T extends { status: string; name: string }>(
  items: T[]
): T[] => {
  return [...items].sort((a, b) => {
    // Active items first
    if (a.status === 'active' && b.status !== 'active') return -1;
    if (a.status !== 'active' && b.status === 'active') return 1;
    // Then alphabetically by name (case-insensitive)
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });
};

/** Sort confluences by confluenceId for order-insensitive comparison */
const sortConfluences = (arr: PlaybookConfluenceLink[]): PlaybookConfluenceLink[] => {
  return [...arr].sort((a, b) => a.confluenceId.localeCompare(b.confluenceId));
};

/**
 * Generate a unique copy name for a playbook.
 * Pattern: "[originalName] (Copy)", then "(Copy 2)", "(Copy 3)", etc.
 * Regex is anchored and case-sensitive to avoid "copy" vs "Copy" clashes.
 */
const generateCopyName = (originalName: string, existingNames: string[]): string => {
  // Normalize existing names to a Set for O(1) lookup
  const existingSet = new Set(existingNames);

  // First try: "[originalName] (Copy)"
  const baseCopyName = `${originalName} (Copy)`;
  if (!existingSet.has(baseCopyName)) {
    return baseCopyName;
  }

  // Find the highest existing copy number for this base name
  // Match patterns like "Name (Copy 2)", "Name (Copy 3)", etc.
  const copyPattern = new RegExp(`^${escapeRegex(originalName)} \\(Copy (\\d+)\\)$`);
  let maxNumber = 1; // (Copy) is implicitly number 1

  for (const name of existingNames) {
    const match = name.match(copyPattern);
    if (match && match[1]) {
      const num = parseInt(match[1], 10);
      if (num > maxNumber) {
        maxNumber = num;
      }
    }
  }

  // Return next available number
  return `${originalName} (Copy ${maxNumber + 1})`;
};

/** Escape special regex characters in a string */
const escapeRegex = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

// =============================================================================
// Types
// =============================================================================

/** Tab keys for playbook linkage tabs (Criteria, Confluences, Entry Models) */
export type LinkageTabKey = 'criteria' | 'confluences' | 'entry-models';

export interface StrategiesPlaybooksContextValue {
  // Hydration state (for URL sync)
  isHydrating: boolean;

  // Strategy data
  strategies: Strategy[];
  selectedStrategy: Strategy | null;
  selectedStrategyId: string | null;
  filteredStrategies: Strategy[];
  activeStrategies: Strategy[];
  archivedStrategies: Strategy[];
  loading: boolean;
  loadError: boolean;
  showArchived: boolean;

  // Strategy dirty state
  localDescription: string;
  localPhilosophy: string;
  isDirty: boolean;
  isSaving: boolean;
  showConflictBanner: boolean;

  // Strategy actions
  selectStrategy: (id: string | null) => void;
  setShowArchived: (show: boolean) => void;
  setLocalDescription: (value: string) => void;
  setLocalPhilosophy: (value: string) => void;
  saveStrategy: () => Promise<void>;
  discardStrategyChanges: () => void;
  archiveStrategy: () => Promise<void>;
  restoreStrategy: () => Promise<void>;
  acceptExternalChanges: () => Promise<void>;
  keepEditing: () => void;
  reloadStrategies: () => Promise<void>;
  /** Optimistically add a newly created strategy to the list (with de-dupe guard) */
  addStrategyToList: (strategy: Strategy) => void;

  // Playbook data
  playbooks: Playbook[];
  selectedPlaybook: Playbook | null;
  selectedPlaybookId: string | null;
  filteredPlaybooks: Playbook[];
  playbooksLoading: boolean;
  playbooksLoadError: boolean;
  showArchivedPlaybooks: boolean;
  /** Which strategy the current playbooks list belongs to (for validation) */
  playbooksForStrategyId: string | null;

  // Playbook dirty state
  localPlaybookDescription: string;
  localPlaybookPhilosophy: string;
  localPlaybookCriteriaIds: string[];
  localPlaybookConfluences: PlaybookConfluenceLink[];
  localPlaybookEntryModelIds: string[];
  isPlaybookDirty: boolean;
  isPlaybookSaving: boolean;
  showPlaybookConflictBanner: boolean;

  // Playbook actions
  selectPlaybook: (id: string | null) => void;
  setShowArchivedPlaybooks: (show: boolean) => void;
  setLocalPlaybookDescription: (value: string) => void;
  setLocalPlaybookPhilosophy: (value: string) => void;
  setLocalPlaybookCriteriaIds: React.Dispatch<React.SetStateAction<string[]>>;
  setLocalPlaybookConfluences: React.Dispatch<React.SetStateAction<PlaybookConfluenceLink[]>>;
  setLocalPlaybookEntryModelIds: React.Dispatch<React.SetStateAction<string[]>>;
  savePlaybook: () => Promise<void>;
  discardPlaybookChanges: () => void;
  restorePlaybook: () => Promise<void>;
  unlinkPlaybook: (id: string) => Promise<void>;
  archivePlaybookFromList: (id: string) => Promise<void>;
  restorePlaybookFromList: (id: string) => Promise<void>;
  acceptPlaybookExternalChanges: () => Promise<void>;
  keepPlaybookEditing: () => void;
  reloadPlaybooks: () => Promise<void>;
  linkPlaybooks: (playbookIds: string[]) => Promise<void>;
  /** Move a playbook from source strategy to target strategy */
  movePlaybook: (playbookId: string, targetStrategyId: string) => Promise<void>;
  /** Copy a playbook to a target strategy (creates new playbook with unique name) */
  copyPlaybook: (playbookId: string, targetStrategyId: string) => Promise<void>;
  /** Share a playbook with one or more target strategies (adds to their playbookIds[]) */
  sharePlaybook: (playbookId: string, targetStrategyIds: string[]) => Promise<void>;
  /** Optimistically add a newly created playbook to the list (with de-dupe guard) */
  addPlaybookToList: (playbook: Playbook) => void;
  /** Optimistically update selected strategy's playbookIds to keep strategies[] fresh */
  updateSelectedStrategyPlaybookIds: (playbookIds: string[]) => void;

  // Linkage tab state (persists across playbook switches)
  activeLinkageTab: LinkageTabKey;
  setActiveLinkageTab: (tab: LinkageTabKey) => void;

  // Playbook details collapse state (persists across playbook switches)
  isPlaybookDetailsExpanded: boolean;
  setIsPlaybookDetailsExpanded: (expanded: boolean) => void;
}

// =============================================================================
// Context
// =============================================================================

const StrategiesPlaybooksContext = createContext<StrategiesPlaybooksContextValue | null>(null);

// =============================================================================
// Provider Props
// =============================================================================

interface StrategiesPlaybooksProviderProps {
  children: React.ReactNode;
}

// =============================================================================
// Provider Implementation
// =============================================================================

export const StrategiesPlaybooksProvider: React.FC<StrategiesPlaybooksProviderProps> = ({
  children,
}) => {
  const { showToast } = useToastNotification();

  // ===========================================================================
  // URL Hydration State (Desktop Only)
  // ===========================================================================

  // Hydration gate: blocks auto-selection effects until URL params are applied
  // Mobile starts false (no URL hydration needed), desktop starts true (wait for URL)
  const [isHydrating, setIsHydrating] = useState(() => {
    // Initial value computed on mount - check if desktop AND has URL params
    if (typeof window === 'undefined') return false;
    const isDesktopInitial = window.matchMedia('(min-width: 768px)').matches;
    if (!isDesktopInitial) return false;
    const params = new URLSearchParams(window.location.search);
    return params.has('strategy') || params.has('playbook');
  });

  // Capture initial URL params on mount (before any effects can modify URL)
  const initialUrlSelectionRef = useRef<{
    strategy: string | null;
    playbook: string | null;
  } | null>(null);
  if (initialUrlSelectionRef.current === null && typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    initialUrlSelectionRef.current = {
      strategy: params.get('strategy'),
      playbook: params.get('playbook'),
    };
  }

  // ===========================================================================
  // Strategy Data State
  // ===========================================================================

  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedStrategyId, setSelectedStrategyId] = useState<string | null>(null);
  const [showArchived, setShowArchivedState] = useState(false);

  // ===========================================================================
  // Strategy Dirty State
  // ===========================================================================

  const [localDescription, setLocalDescription] = useState('');
  const lastSavedDescriptionRef = useRef('');
  const [localPhilosophy, setLocalPhilosophy] = useState('');
  const lastSavedPhilosophyRef = useRef('');
  const [isSaving, setIsSaving] = useState(false);

  // Sync conflict state
  const [hasQueuedUpdate, setHasQueuedUpdate] = useState(false);
  const [dismissedExternalUpdate, setDismissedExternalUpdate] = useState(false);

  // Computed dirty state
  const isDirty =
    localDescription !== lastSavedDescriptionRef.current ||
    localPhilosophy !== lastSavedPhilosophyRef.current;
  const showConflictBanner = hasQueuedUpdate && isDirty && !dismissedExternalUpdate;

  // ===========================================================================
  // Playbook Data State
  // ===========================================================================

  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  const [playbooksLoading, setPlaybooksLoading] = useState(false);
  const [playbooksLoadError, setPlaybooksLoadError] = useState(false);
  const [showArchivedPlaybooks, setShowArchivedPlaybooksState] = useState(false);
  const [selectedPlaybookId, setSelectedPlaybookId] = useState<string | null>(null);

  // Track previous strategy's playbookIds for sync detection
  const prevPlaybookIdsRef = useRef<string[] | undefined>(undefined);

  // Track which strategy the current playbooks list belongs to (for hydration validation)
  const playbooksForStrategyIdRef = useRef<string | null>(null);

  // ===========================================================================
  // Playbook Dirty State
  // ===========================================================================

  const [localPlaybookDescription, setLocalPlaybookDescription] = useState('');
  const lastSavedPlaybookDescriptionRef = useRef('');
  const [localPlaybookPhilosophy, setLocalPlaybookPhilosophy] = useState('');
  const lastSavedPlaybookPhilosophyRef = useRef('');
  const [localPlaybookCriteriaIds, setLocalPlaybookCriteriaIds] = useState<string[]>([]);
  const lastSavedPlaybookCriteriaIdsRef = useRef<string[]>([]);
  const [localPlaybookConfluences, setLocalPlaybookConfluences] = useState<PlaybookConfluenceLink[]>([]);
  const lastSavedPlaybookConfluencesRef = useRef<PlaybookConfluenceLink[]>([]);
  const [localPlaybookEntryModelIds, setLocalPlaybookEntryModelIds] = useState<string[]>([]);
  const lastSavedPlaybookEntryModelIdsRef = useRef<string[]>([]);
  const [isPlaybookSaving, setIsPlaybookSaving] = useState(false);

  // Playbook sync conflict state
  const [hasPlaybookQueuedUpdate, setHasPlaybookQueuedUpdate] = useState(false);
  const [dismissedPlaybookExternalUpdate, setDismissedPlaybookExternalUpdate] = useState(false);

  // Computed playbook dirty state
  // Sort criteriaIds, confluences, and entryModelIds before comparison since order is not meaningful
  const isPlaybookDirty =
    localPlaybookDescription !== lastSavedPlaybookDescriptionRef.current ||
    localPlaybookPhilosophy !== lastSavedPlaybookPhilosophyRef.current ||
    JSON.stringify([...localPlaybookCriteriaIds].sort()) !==
      JSON.stringify([...lastSavedPlaybookCriteriaIdsRef.current].sort()) ||
    JSON.stringify(sortConfluences(localPlaybookConfluences)) !==
      JSON.stringify(sortConfluences(lastSavedPlaybookConfluencesRef.current)) ||
    JSON.stringify([...localPlaybookEntryModelIds].sort()) !==
      JSON.stringify([...lastSavedPlaybookEntryModelIdsRef.current].sort());
  const showPlaybookConflictBanner =
    hasPlaybookQueuedUpdate && isPlaybookDirty && !dismissedPlaybookExternalUpdate;

  // ===========================================================================
  // Linkage Tab State (persists across playbook switches)
  // ===========================================================================

  const [activeLinkageTab, setActiveLinkageTabState] = useState<LinkageTabKey>('criteria');

  const setActiveLinkageTab = useCallback((tab: LinkageTabKey) => {
    setActiveLinkageTabState(tab);
  }, []);

  // ===========================================================================
  // Playbook Details Collapse State (persists across playbook switches)
  // ===========================================================================

  const [isPlaybookDetailsExpanded, setIsPlaybookDetailsExpandedState] = useState<boolean>(false);

  const setIsPlaybookDetailsExpanded = useCallback((expanded: boolean) => {
    setIsPlaybookDetailsExpandedState(expanded);
  }, []);

  // ===========================================================================
  // Refs for Sync Callbacks (sync during render body, not useEffect)
  // ===========================================================================

  const isDirtyRef = useRef(isDirty);
  const isPlaybookDirtyRef = useRef(isPlaybookDirty);
  const selectedStrategyIdRef = useRef(selectedStrategyId);

  // Track previous synced IDs to detect ID change vs same-ID update
  const prevSyncedStrategyIdRef = useRef<string | null>(null);
  const prevSyncedPlaybookIdRef = useRef<string | null>(null);

  // Sync refs during render body per CODING_STANDARDS
  isDirtyRef.current = isDirty;
  isPlaybookDirtyRef.current = isPlaybookDirty;
  selectedStrategyIdRef.current = selectedStrategyId;

  // ===========================================================================
  // Filtered & Selected Strategy (Memoized)
  // ===========================================================================

  const filteredStrategies = useMemo(() => {
    const filtered = strategies.filter((s) => {
      if (s.status === 'active') return true;
      if (showArchived && s.status === 'archived') return true;
      return false;
    });
    return sortByStatusThenName(filtered);
  }, [strategies, showArchived]);

  const selectedStrategy = useMemo(() => {
    if (!selectedStrategyId) return null;
    return strategies.find((s) => s.id === selectedStrategyId) ?? null;
  }, [strategies, selectedStrategyId]);

  const activeStrategies = useMemo(
    () =>
      filteredStrategies
        .filter((s) => s.status === 'active')
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [filteredStrategies]
  );

  const archivedStrategies = useMemo(
    () =>
      filteredStrategies
        .filter((s) => s.status === 'archived')
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [filteredStrategies]
  );

  // ===========================================================================
  // Filtered & Selected Playbook (Memoized)
  // ===========================================================================

  const filteredPlaybooks = useMemo(() => {
    const filtered = playbooks.filter((p) => {
      if (p.status === 'active') return true;
      if (showArchivedPlaybooks && p.status === 'archived') return true;
      return false;
    });
    return sortByStatusThenName(filtered);
  }, [playbooks, showArchivedPlaybooks]);

  const selectedPlaybook = useMemo(() => {
    if (!selectedPlaybookId) return null;
    return playbooks.find((p) => p.id === selectedPlaybookId) ?? null;
  }, [playbooks, selectedPlaybookId]);

  // ===========================================================================
  // Data Loading Functions
  // ===========================================================================

  const loadStrategies = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const allStrategies = await strategyService.getAll();
      setStrategies(allStrategies);
    } catch (error) {
      console.error('Failed to load strategies:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPlaybooks = useCallback(async (strategyId: string | null) => {
    if (!strategyId) {
      setPlaybooks([]);
      setPlaybooksLoadError(false);
      playbooksForStrategyIdRef.current = null;
      return;
    }

    try {
      setPlaybooksLoading(true);
      setPlaybooksLoadError(false);
      const strategyPlaybooks = await playbookService.getByStrategyId(strategyId);
      setPlaybooks(strategyPlaybooks);
      playbooksForStrategyIdRef.current = strategyId;
    } catch (error) {
      console.error('Failed to load playbooks:', error);
      setPlaybooksLoadError(true);
      playbooksForStrategyIdRef.current = null;
    } finally {
      setPlaybooksLoading(false);
    }
  }, []);

  // ===========================================================================
  // Initial Data Load
  // ===========================================================================

  useEffect(() => {
    loadStrategies();
  }, [loadStrategies]);

  // ===========================================================================
  // Strategy Sync Subscription
  // ===========================================================================

  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('strategies', () => {
      if (isDirtyRef.current) {
        // Queue update, don't apply immediately
        setHasQueuedUpdate(true);
        setDismissedExternalUpdate(false);
      } else {
        // Not dirty, reload immediately
        loadStrategies();
      }
    });

    return () => unsubscribe();
  }, [loadStrategies]);

  // ===========================================================================
  // Playbook Sync Subscription
  // ===========================================================================

  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('playbooks', async () => {
      if (isPlaybookDirtyRef.current) {
        // Queue update, don't apply immediately
        setHasPlaybookQueuedUpdate(true);
        setDismissedPlaybookExternalUpdate(false);
      } else {
        // Not dirty, reload immediately
        // On playbook sync, reload strategies first to get updated playbookIds
        await loadStrategies();
        if (selectedStrategyIdRef.current) {
          await loadPlaybooks(selectedStrategyIdRef.current);
        }
      }
    });

    return () => unsubscribe();
  }, [loadStrategies, loadPlaybooks]);

  // ===========================================================================
  // Auto-Selection Effects
  // ===========================================================================

  // Auto-select first strategy when data loads (if none selected)
  useEffect(() => {
    // Gate: skip during URL hydration to avoid race conditions
    if (isHydrating) return;
    const firstStrategy = filteredStrategies[0];
    if (!loading && firstStrategy && !selectedStrategyId) {
      setSelectedStrategyId(firstStrategy.id);
    }
  }, [loading, filteredStrategies, selectedStrategyId, isHydrating]);

  // Handle selection becoming unavailable
  useEffect(() => {
    // Gate: skip during URL hydration to avoid race conditions
    if (isHydrating) return;
    if (!selectedStrategy && selectedStrategyId && !loading) {
      // Only fall back if the selected ID truly doesn't exist in strategies
      // (not just temporarily missing from filteredStrategies during filter changes)
      const existsInStrategies = strategies.some((s) => s.id === selectedStrategyId);
      if (!existsInStrategies) {
        // Selected strategy was deleted or tombstoned - auto-select first available
        const firstStrategy = filteredStrategies[0];
        if (firstStrategy) {
          setSelectedStrategyId(firstStrategy.id);
        } else {
          setSelectedStrategyId(null);
        }
      }
    }
  }, [selectedStrategy, selectedStrategyId, filteredStrategies, loading, strategies, isHydrating]);

  // Auto-show archived if selected strategy becomes archived
  useEffect(() => {
    if (selectedStrategy?.status === 'archived' && !showArchived && !loading) {
      setShowArchivedState(true);
    }
  }, [selectedStrategy?.status, showArchived, loading]);

  // Load playbooks when selected strategy changes + reset playbook state
  useEffect(() => {
    loadPlaybooks(selectedStrategyId);

    // Reset all playbook state on strategy change
    setSelectedPlaybookId(null);
    setLocalPlaybookDescription('');
    lastSavedPlaybookDescriptionRef.current = '';
    setLocalPlaybookPhilosophy('');
    lastSavedPlaybookPhilosophyRef.current = '';
    setLocalPlaybookCriteriaIds([]);
    lastSavedPlaybookCriteriaIdsRef.current = [];
    setLocalPlaybookConfluences([]);
    lastSavedPlaybookConfluencesRef.current = [];
    setLocalPlaybookEntryModelIds([]);
    lastSavedPlaybookEntryModelIdsRef.current = [];
    setHasPlaybookQueuedUpdate(false);
    setDismissedPlaybookExternalUpdate(false);
    setShowArchivedPlaybooksState(false);

    // Reset prevPlaybookIdsRef for the new strategy
    prevPlaybookIdsRef.current = undefined;
  }, [selectedStrategyId, loadPlaybooks]);

  // Reload playbooks when strategy's playbookIds change (e.g., via sync from another tab)
  useEffect(() => {
    const currentPlaybookIds = selectedStrategy?.playbookIds;
    const prevPlaybookIds = prevPlaybookIdsRef.current;

    // Only reload if playbookIds actually changed (not on initial mount)
    if (prevPlaybookIds !== undefined && selectedStrategyId) {
      const currentIds = currentPlaybookIds ?? [];
      const prevIds = prevPlaybookIds ?? [];
      const hasChanged =
        currentIds.length !== prevIds.length ||
        currentIds.some((id, idx) => id !== prevIds[idx]);

      if (hasChanged) {
        loadPlaybooks(selectedStrategyId);
      }
    }

    // Update ref for next comparison
    prevPlaybookIdsRef.current = currentPlaybookIds;
  }, [selectedStrategy?.playbookIds, selectedStrategyId, loadPlaybooks]);

  // Auto-select first playbook when data loads (if none selected)
  useEffect(() => {
    // Gate: skip during URL hydration to avoid race conditions
    if (isHydrating) return;
    const firstPlaybook = filteredPlaybooks[0];
    if (!playbooksLoading && firstPlaybook && !selectedPlaybookId && selectedStrategyId) {
      setSelectedPlaybookId(firstPlaybook.id);
    }
  }, [playbooksLoading, filteredPlaybooks, selectedPlaybookId, selectedStrategyId, isHydrating]);

  // Auto-show archived if selected playbook becomes archived
  useEffect(() => {
    if (selectedPlaybook?.status === 'archived' && !showArchivedPlaybooks && !playbooksLoading) {
      setShowArchivedPlaybooksState(true);
    }
  }, [selectedPlaybook?.status, showArchivedPlaybooks, playbooksLoading]);

  // Handle selected playbook becoming unavailable
  useEffect(() => {
    // Gate: skip during URL hydration to avoid race conditions
    if (isHydrating) return;
    if (selectedPlaybookId && !selectedPlaybook && !playbooksLoading) {
      // Only fall back if the selected ID truly doesn't exist in playbooks
      // (not just temporarily missing from filteredPlaybooks during filter changes)
      const existsInPlaybooks = playbooks.some((p) => p.id === selectedPlaybookId);
      if (!existsInPlaybooks) {
        // Selected playbook was deleted or unlinked - auto-select first available
        const firstFiltered = filteredPlaybooks[0];
        if (firstFiltered) {
          setSelectedPlaybookId(firstFiltered.id);
        } else {
          setSelectedPlaybookId(null);
        }
      }
    }
  }, [selectedPlaybookId, selectedPlaybook, playbooksLoading, filteredPlaybooks, playbooks, isHydrating]);

  // ===========================================================================
  // URL Hydration Effect (Desktop Only)
  // ===========================================================================

  // Apply URL selection on initial load, then unlock auto-selection effects
  useEffect(() => {
    // Only run while hydrating
    if (!isHydrating) return;

    // Wait for strategies to load
    if (loading) return;

    const urlStrategyId = initialUrlSelectionRef.current?.strategy;
    const urlPlaybookId = initialUrlSelectionRef.current?.playbook;

    // If no URL params, complete hydration immediately
    if (!urlStrategyId && !urlPlaybookId) {
      setIsHydrating(false);
      return;
    }

    // Apply strategy selection from URL
    if (urlStrategyId) {
      const strategy = strategies.find((s) => s.id === urlStrategyId);
      if (strategy) {
        // Enable showArchived if strategy is archived
        if (strategy.status === 'archived' && !showArchived) {
          setShowArchivedState(true);
        }
        // Select the strategy if not already selected
        if (selectedStrategyId !== urlStrategyId) {
          setSelectedStrategyId(urlStrategyId);
          return; // Wait for next render after selection
        }
      } else {
        // Invalid strategy ID - complete hydration, let auto-select pick first
        setIsHydrating(false);
        return;
      }
    }

    // Early completion: no playbook param in URL
    if (!urlPlaybookId) {
      setIsHydrating(false);
      return;
    }

    // Early completion: strategy has zero playbooks
    const strategyPlaybookIds = selectedStrategy?.playbookIds ?? [];
    if (strategyPlaybookIds.length === 0 && !playbooksLoading) {
      setIsHydrating(false);
      return;
    }

    // Wait for playbooks to load
    if (playbooksLoading) return;

    // Guard: ensure playbooks belong to the URL strategy (not stale from previous selection)
    if (urlPlaybookId && playbooksForStrategyIdRef.current !== urlStrategyId) return;

    // Apply playbook selection from URL
    const playbook = playbooks.find((p) => p.id === urlPlaybookId);
    if (playbook) {
      // Enable showArchivedPlaybooks if playbook is archived
      if (playbook.status === 'archived' && !showArchivedPlaybooks) {
        setShowArchivedPlaybooksState(true);
      }
      // Select the playbook if not already selected
      if (selectedPlaybookId !== urlPlaybookId) {
        setSelectedPlaybookId(urlPlaybookId);
        return; // Wait for next render after selection
      }
    }
    // Invalid playbook ID or selection complete - finish hydration

    setIsHydrating(false);
  }, [
    isHydrating,
    loading,
    strategies,
    selectedStrategyId,
    selectedStrategy,
    showArchived,
    playbooksLoading,
    playbooks,
    selectedPlaybookId,
    showArchivedPlaybooks,
  ]);

  // ===========================================================================
  // Sync Local State with Selected Strategy
  // ===========================================================================

  useEffect(() => {
    const isIdChange = selectedStrategy?.id !== prevSyncedStrategyIdRef.current;
    prevSyncedStrategyIdRef.current = selectedStrategy?.id ?? null;

    if (selectedStrategy) {
      // Always sync on ID change (selection change)
      // Only sync on updatedAt change if not dirty (cross-tab update)
      if (isIdChange || !isDirtyRef.current) {
        const description = selectedStrategy.description ?? '';
        const philosophy = selectedStrategy.philosophy ?? '';
        setLocalDescription(description);
        lastSavedDescriptionRef.current = description;
        setLocalPhilosophy(philosophy);
        lastSavedPhilosophyRef.current = philosophy;
      }
      // Reset conflict state only on ID change
      if (isIdChange) {
        setHasQueuedUpdate(false);
        setDismissedExternalUpdate(false);
      }
    } else {
      setLocalDescription('');
      lastSavedDescriptionRef.current = '';
      setLocalPhilosophy('');
      lastSavedPhilosophyRef.current = '';
    }
  }, [selectedStrategy]);

  // ===========================================================================
  // Sync Local State with Selected Playbook
  // ===========================================================================

  useEffect(() => {
    const isIdChange = selectedPlaybook?.id !== prevSyncedPlaybookIdRef.current;
    prevSyncedPlaybookIdRef.current = selectedPlaybook?.id ?? null;

    if (selectedPlaybook) {
      // Always sync on ID change (selection change)
      // Only sync on updatedAt change if not dirty (cross-tab update)
      if (isIdChange || !isPlaybookDirtyRef.current) {
        const description = selectedPlaybook.description ?? '';
        const philosophy = selectedPlaybook.philosophy ?? '';
        const criteriaIds = selectedPlaybook.criteriaIds ?? [];
        const confluences = sortConfluences(selectedPlaybook.confluences ?? []);
        const entryModelIds = selectedPlaybook.entryModelIds ?? [];
        setLocalPlaybookDescription(description);
        lastSavedPlaybookDescriptionRef.current = description;
        setLocalPlaybookPhilosophy(philosophy);
        lastSavedPlaybookPhilosophyRef.current = philosophy;
        setLocalPlaybookCriteriaIds(criteriaIds);
        lastSavedPlaybookCriteriaIdsRef.current = criteriaIds;
        setLocalPlaybookConfluences(confluences);
        lastSavedPlaybookConfluencesRef.current = confluences;
        setLocalPlaybookEntryModelIds(entryModelIds);
        lastSavedPlaybookEntryModelIdsRef.current = entryModelIds;
      }
      // Reset conflict state only on ID change
      if (isIdChange) {
        setHasPlaybookQueuedUpdate(false);
        setDismissedPlaybookExternalUpdate(false);
      }
    } else {
      setLocalPlaybookDescription('');
      lastSavedPlaybookDescriptionRef.current = '';
      setLocalPlaybookPhilosophy('');
      lastSavedPlaybookPhilosophyRef.current = '';
      setLocalPlaybookCriteriaIds([]);
      lastSavedPlaybookCriteriaIdsRef.current = [];
      setLocalPlaybookConfluences([]);
      lastSavedPlaybookConfluencesRef.current = [];
      setLocalPlaybookEntryModelIds([]);
      lastSavedPlaybookEntryModelIdsRef.current = [];
    }
  }, [selectedPlaybook]);

  // ===========================================================================
  // Strategy Actions
  // ===========================================================================

  const selectStrategy = useCallback((id: string | null) => {
    setSelectedStrategyId(id);
  }, []);

  const setShowArchived = useCallback(
    (show: boolean) => {
      if (!show && selectedStrategy?.status === 'archived') {
        // Turning off while archived strategy selected - auto-select first active
        const firstActive = strategies.find((s) => s.status === 'active');
        if (firstActive) {
          setSelectedStrategyId(firstActive.id);
        } else {
          setSelectedStrategyId(null);
        }
      }
      setShowArchivedState(show);
    },
    [selectedStrategy, strategies]
  );

  const saveStrategy = useCallback(async () => {
    if (!selectedStrategy || !isDirty) return;

    setIsSaving(true);
    try {
      await strategyService.update(selectedStrategy.id, {
        description: localDescription,
        philosophy: localPhilosophy,
      });
      syncCoordinatorService.notifyChange('strategies', selectedStrategy.id);
      lastSavedDescriptionRef.current = localDescription;
      lastSavedPhilosophyRef.current = localPhilosophy;
      setHasQueuedUpdate(false);
      showToast({
        title: 'Strategy Saved',
        description: `"${selectedStrategy.name}" updated`,
        color: 'success',
      });
      await loadStrategies();
    } catch (error) {
      console.error('Failed to save strategy:', error);
      showToast({
        title: 'Save Failed',
        description: 'Could not save strategy. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  }, [selectedStrategy, isDirty, localDescription, localPhilosophy, showToast, loadStrategies]);

  const discardStrategyChanges = useCallback(() => {
    setLocalDescription(lastSavedDescriptionRef.current);
    setLocalPhilosophy(lastSavedPhilosophyRef.current);
    // If there's a queued external update, apply it now
    if (hasQueuedUpdate) {
      setHasQueuedUpdate(false);
      setDismissedExternalUpdate(false);
      loadStrategies();
    }
  }, [hasQueuedUpdate, loadStrategies]);

  const archiveStrategy = useCallback(async () => {
    if (!selectedStrategy) return;

    setIsSaving(true);
    try {
      await strategyService.update(selectedStrategy.id, { status: 'archived' });
      syncCoordinatorService.notifyChange('strategies', selectedStrategy.id);
      // Auto-toggle showArchived on to keep selection visible
      setShowArchivedState(true);
      showToast({
        title: 'Strategy Archived',
        description: `"${selectedStrategy.name}" has been archived`,
        color: 'success',
      });
      await loadStrategies();
    } catch (error) {
      console.error('Failed to archive strategy:', error);
      showToast({
        title: 'Archive Failed',
        description: 'Could not archive strategy. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  }, [selectedStrategy, showToast, loadStrategies]);

  const restoreStrategy = useCallback(async () => {
    if (!selectedStrategy) return;

    setIsSaving(true);
    try {
      await strategyService.update(selectedStrategy.id, { status: 'active' });
      syncCoordinatorService.notifyChange('strategies', selectedStrategy.id);
      showToast({
        title: 'Strategy Restored',
        description: `"${selectedStrategy.name}" is now active`,
        color: 'success',
      });
      await loadStrategies();
    } catch (error) {
      console.error('Failed to restore strategy:', error);
      showToast({
        title: 'Restore Failed',
        description: 'Could not restore strategy. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  }, [selectedStrategy, showToast, loadStrategies]);

  const acceptExternalChanges = useCallback(async () => {
    if (!selectedStrategyId) return;

    // Reload strategies list
    await loadStrategies();

    // Fetch fresh strategy and apply fields directly
    const freshStrategy = await strategyService.get(selectedStrategyId);
    if (freshStrategy) {
      const description = freshStrategy.description ?? '';
      const philosophy = freshStrategy.philosophy ?? '';
      setLocalDescription(description);
      lastSavedDescriptionRef.current = description;
      setLocalPhilosophy(philosophy);
      lastSavedPhilosophyRef.current = philosophy;
    } else {
      // Strategy was deleted - notify user and clear selection
      showToast({
        title: 'Strategy No Longer Exists',
        description: 'This strategy was deleted in another tab.',
        color: 'warning',
      });
      setSelectedStrategyId(null);
      setLocalDescription('');
      lastSavedDescriptionRef.current = '';
      setLocalPhilosophy('');
      lastSavedPhilosophyRef.current = '';
    }

    // Clear conflict state
    setHasQueuedUpdate(false);
    setDismissedExternalUpdate(false);
  }, [loadStrategies, selectedStrategyId, showToast]);

  const keepEditing = useCallback(() => {
    setDismissedExternalUpdate(true);
  }, []);

  const reloadStrategies = useCallback(async () => {
    await loadStrategies();
  }, [loadStrategies]);

  /**
   * Optimistically add a newly created strategy to the list.
   * De-dupes by id in case sync subscription fires before/during insert.
   */
  const addStrategyToList = useCallback((strategy: Strategy) => {
    setStrategies((prev) =>
      prev.some((s) => s.id === strategy.id) ? prev : [...prev, strategy]
    );
  }, []);

  /**
   * Optimistically update the selected strategy's playbookIds in strategies[].
   * Keeps strategies list fresh after playbook create/link without full reload.
   */
  const updateSelectedStrategyPlaybookIds = useCallback(
    (playbookIds: string[]) => {
      if (!selectedStrategyId) return;
      setStrategies((prev) =>
        prev.map((s) =>
          s.id === selectedStrategyId ? { ...s, playbookIds } : s
        )
      );
    },
    [selectedStrategyId]
  );

  // ===========================================================================
  // Playbook Actions
  // ===========================================================================

  const selectPlaybook = useCallback((id: string | null) => {
    setSelectedPlaybookId(id);
  }, []);

  const setShowArchivedPlaybooks = useCallback(
    (show: boolean) => {
      if (!show && selectedPlaybook?.status === 'archived') {
        // Turning off while archived playbook selected - auto-select first active
        const firstActive = playbooks.find((p) => p.status === 'active');
        if (firstActive) {
          setSelectedPlaybookId(firstActive.id);
        } else {
          setSelectedPlaybookId(null);
        }
      }
      setShowArchivedPlaybooksState(show);
    },
    [selectedPlaybook, playbooks]
  );

  const savePlaybook = useCallback(async () => {
    if (!selectedPlaybook || !isPlaybookDirty) return;

    setIsPlaybookSaving(true);
    try {
      await playbookService.update(selectedPlaybook.id, {
        description: localPlaybookDescription,
        philosophy: localPlaybookPhilosophy,
        criteriaIds: localPlaybookCriteriaIds,
        confluences: localPlaybookConfluences,
        entryModelIds: localPlaybookEntryModelIds,
      });
      syncCoordinatorService.notifyChange('playbooks', selectedPlaybook.id);
      // Update refs AFTER successful save (normalize confluence order)
      lastSavedPlaybookDescriptionRef.current = localPlaybookDescription;
      lastSavedPlaybookPhilosophyRef.current = localPlaybookPhilosophy;
      lastSavedPlaybookCriteriaIdsRef.current = localPlaybookCriteriaIds;
      lastSavedPlaybookConfluencesRef.current = sortConfluences(localPlaybookConfluences);
      lastSavedPlaybookEntryModelIdsRef.current = localPlaybookEntryModelIds;
      setHasPlaybookQueuedUpdate(false);
      showToast({
        title: 'Playbook Saved',
        description: `"${selectedPlaybook.name}" updated`,
        color: 'success',
      });
      // Reload to ensure UI is in sync
      if (selectedStrategyId) {
        await loadPlaybooks(selectedStrategyId);
      }
    } catch (error) {
      console.error('Failed to save playbook:', error);
      showToast({
        title: 'Save Failed',
        description: 'Could not save playbook. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsPlaybookSaving(false);
    }
  }, [
    selectedPlaybook,
    isPlaybookDirty,
    localPlaybookDescription,
    localPlaybookPhilosophy,
    localPlaybookCriteriaIds,
    localPlaybookConfluences,
    localPlaybookEntryModelIds,
    showToast,
    loadPlaybooks,
    selectedStrategyId,
  ]);

  const discardPlaybookChanges = useCallback(() => {
    setLocalPlaybookDescription(lastSavedPlaybookDescriptionRef.current);
    setLocalPlaybookPhilosophy(lastSavedPlaybookPhilosophyRef.current);
    setLocalPlaybookCriteriaIds(lastSavedPlaybookCriteriaIdsRef.current);
    setLocalPlaybookConfluences(lastSavedPlaybookConfluencesRef.current);
    setLocalPlaybookEntryModelIds(lastSavedPlaybookEntryModelIdsRef.current);
    // If there's a queued external update, apply it now
    if (hasPlaybookQueuedUpdate) {
      setHasPlaybookQueuedUpdate(false);
      setDismissedPlaybookExternalUpdate(false);
      loadStrategies();
      if (selectedStrategyId) {
        loadPlaybooks(selectedStrategyId);
      }
    }
  }, [hasPlaybookQueuedUpdate, loadStrategies, loadPlaybooks, selectedStrategyId]);

  const restorePlaybook = useCallback(async () => {
    if (!selectedPlaybook) return;

    setIsPlaybookSaving(true);
    try {
      await playbookService.update(selectedPlaybook.id, { status: 'active' });
      syncCoordinatorService.notifyChange('playbooks', selectedPlaybook.id);
      showToast({
        title: 'Playbook Restored',
        description: `"${selectedPlaybook.name}" is now active`,
        color: 'success',
      });
      if (selectedStrategyId) {
        await loadPlaybooks(selectedStrategyId);
      }
    } catch (error) {
      console.error('Failed to restore playbook:', error);
      showToast({
        title: 'Restore Failed',
        description: 'Could not restore playbook. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsPlaybookSaving(false);
    }
  }, [selectedPlaybook, showToast, loadPlaybooks, selectedStrategyId]);

  const unlinkPlaybook = useCallback(
    async (playbookId: string) => {
      if (!selectedStrategy) return;

      setIsPlaybookSaving(true);
      try {
        // Fetch ALL strategies fresh to avoid stale snapshot race condition
        const allStrategies = await strategyService.getAll();

        // Count how many strategies reference this playbook
        const referenceCount = allStrategies.filter((s) =>
          s.playbookIds?.includes(playbookId)
        ).length;
        const isOnlyUsedHere = referenceCount === 1;

        // Find current strategy in fresh list
        const freshStrategy = allStrategies.find((s) => s.id === selectedStrategy.id);
        if (!freshStrategy) {
          showToast({
            title: 'Unlink Failed',
            description: 'Strategy not found.',
            color: 'danger',
          });
          return;
        }

        // Get playbook for toast message and status check
        const playbook = playbooks.find((p) => p.id === playbookId);
        const playbookName = playbook?.name || 'Playbook';

        // Remove playbook from strategy's playbookIds
        const updatedIds = (freshStrategy.playbookIds || []).filter((id) => id !== playbookId);
        await strategyService.update(selectedStrategy.id, { playbookIds: updatedIds });
        syncCoordinatorService.notifyChange('strategies', selectedStrategy.id);

        // If orphaned and not already archived, auto-archive the playbook
        let wasAutoArchived = false;
        if (isOnlyUsedHere && playbook && playbook.status !== 'archived') {
          await playbookService.update(playbookId, { status: 'archived' });
          syncCoordinatorService.notifyChange('playbooks', playbookId);
          wasAutoArchived = true;
        }

        // Reload data
        await loadStrategies();
        if (selectedStrategyId) {
          await loadPlaybooks(selectedStrategyId);
        }

        // Show appropriate toast
        if (wasAutoArchived) {
          showToast({
            title: 'Playbook Unlinked & Archived',
            description: `"${playbookName}" was archived since it's no longer used by any strategy.`,
            color: 'success',
          });
          setShowArchivedPlaybooksState(true);
        } else {
          showToast({
            title: 'Playbook Unlinked',
            description: `"${playbookName}" removed from this strategy.`,
            color: 'success',
          });
        }
      } catch (error) {
        console.error('Failed to unlink playbook:', error);
        showToast({
          title: 'Unlink Failed',
          description: 'Could not unlink playbook. Please try again.',
          color: 'danger',
        });
      } finally {
        setIsPlaybookSaving(false);
      }
    },
    [selectedStrategy, selectedStrategyId, playbooks, showToast, loadStrategies, loadPlaybooks]
  );

  const archivePlaybookFromList = useCallback(
    async (playbookId: string) => {
      const playbook = playbooks.find((p) => p.id === playbookId);
      if (!playbook) return;

      setIsPlaybookSaving(true);
      try {
        await playbookService.update(playbookId, { status: 'archived' });
        syncCoordinatorService.notifyChange('playbooks', playbookId);
        // Auto-toggle showArchivedPlaybooks on so user sees the archived item
        setShowArchivedPlaybooksState(true);
        showToast({
          title: 'Playbook Archived',
          description: `"${playbook.name}" has been archived`,
          color: 'success',
        });
        if (selectedStrategyId) {
          await loadPlaybooks(selectedStrategyId);
        }
      } catch (error) {
        console.error('Failed to archive playbook:', error);
        showToast({
          title: 'Archive Failed',
          description: 'Could not archive playbook. Please try again.',
          color: 'danger',
        });
      } finally {
        setIsPlaybookSaving(false);
      }
    },
    [playbooks, selectedStrategyId, showToast, loadPlaybooks]
  );

  const restorePlaybookFromList = useCallback(
    async (playbookId: string) => {
      const playbook = playbooks.find((p) => p.id === playbookId);
      if (!playbook) return;

      setIsPlaybookSaving(true);
      try {
        await playbookService.update(playbookId, { status: 'active' });
        syncCoordinatorService.notifyChange('playbooks', playbookId);
        showToast({
          title: 'Playbook Restored',
          description: `"${playbook.name}" is now active`,
          color: 'success',
        });
        if (selectedStrategyId) {
          await loadPlaybooks(selectedStrategyId);
        }
      } catch (error) {
        console.error('Failed to restore playbook:', error);
        showToast({
          title: 'Restore Failed',
          description: 'Could not restore playbook. Please try again.',
          color: 'danger',
        });
      } finally {
        setIsPlaybookSaving(false);
      }
    },
    [playbooks, selectedStrategyId, showToast, loadPlaybooks]
  );

  const acceptPlaybookExternalChanges = useCallback(async () => {
    if (!selectedPlaybookId) return;

    // Reload data
    await loadStrategies();
    if (selectedStrategyId) {
      await loadPlaybooks(selectedStrategyId);
    }

    // Fetch fresh playbook and apply fields directly
    const freshPlaybook = await playbookService.get(selectedPlaybookId);
    if (freshPlaybook) {
      const description = freshPlaybook.description ?? '';
      const philosophy = freshPlaybook.philosophy ?? '';
      const criteriaIds = freshPlaybook.criteriaIds ?? [];
      const confluences = sortConfluences(freshPlaybook.confluences ?? []);
      const entryModelIds = freshPlaybook.entryModelIds ?? [];
      setLocalPlaybookDescription(description);
      lastSavedPlaybookDescriptionRef.current = description;
      setLocalPlaybookPhilosophy(philosophy);
      lastSavedPlaybookPhilosophyRef.current = philosophy;
      setLocalPlaybookCriteriaIds(criteriaIds);
      lastSavedPlaybookCriteriaIdsRef.current = criteriaIds;
      setLocalPlaybookConfluences(confluences);
      lastSavedPlaybookConfluencesRef.current = confluences;
      setLocalPlaybookEntryModelIds(entryModelIds);
      lastSavedPlaybookEntryModelIdsRef.current = entryModelIds;
    } else {
      // Playbook was deleted - notify user and clear selection
      showToast({
        title: 'Playbook No Longer Exists',
        description: 'This playbook was deleted in another tab.',
        color: 'warning',
      });
      setSelectedPlaybookId(null);
      setLocalPlaybookDescription('');
      lastSavedPlaybookDescriptionRef.current = '';
      setLocalPlaybookPhilosophy('');
      lastSavedPlaybookPhilosophyRef.current = '';
      setLocalPlaybookCriteriaIds([]);
      lastSavedPlaybookCriteriaIdsRef.current = [];
      setLocalPlaybookConfluences([]);
      lastSavedPlaybookConfluencesRef.current = [];
      setLocalPlaybookEntryModelIds([]);
      lastSavedPlaybookEntryModelIdsRef.current = [];
    }

    // Clear conflict state
    setHasPlaybookQueuedUpdate(false);
    setDismissedPlaybookExternalUpdate(false);
  }, [loadStrategies, loadPlaybooks, selectedStrategyId, selectedPlaybookId, showToast]);

  const keepPlaybookEditing = useCallback(() => {
    setDismissedPlaybookExternalUpdate(true);
  }, []);

  const reloadPlaybooks = useCallback(async () => {
    if (selectedStrategyId) {
      await loadPlaybooks(selectedStrategyId);
    }
  }, [loadPlaybooks, selectedStrategyId]);

  const linkPlaybooks = useCallback(
    async (playbookIds: string[]) => {
      if (!selectedStrategy || playbookIds.length === 0) return;

      try {
        // Fetch fresh strategy to avoid stale state
        const freshStrategy = await strategyService.get(selectedStrategy.id);
        if (!freshStrategy) {
          showToast({
            title: 'Link Failed',
            description: 'Strategy not found. It may have been deleted.',
            color: 'danger',
          });
          return;
        }

        // De-duplicate when appending using fresh data
        const existingIds = freshStrategy.playbookIds || [];
        const deduplicatedIds = Array.from(new Set([...existingIds, ...playbookIds]));

        await strategyService.update(selectedStrategy.id, {
          playbookIds: deduplicatedIds,
        });
        syncCoordinatorService.notifyChange('strategies', selectedStrategy.id);

        // Use actual added count
        const addedCount = deduplicatedIds.length - existingIds.length;
        showToast({
          title: addedCount === 1 ? 'Playbook Linked' : 'Playbooks Linked',
          description: `${addedCount} playbook${addedCount === 1 ? '' : 's'} added to "${freshStrategy.name}"`,
          color: 'success',
        });

        // Reload data
        await loadStrategies();
        if (selectedStrategyId) {
          await loadPlaybooks(selectedStrategyId);
        }

        // Select first newly linked playbook
        const firstLinked = playbookIds[0];
        if (firstLinked) {
          setSelectedPlaybookId(firstLinked);
        }
      } catch (error) {
        console.error('Failed to link playbooks:', error);
        showToast({
          title: 'Link Failed',
          description: 'Could not link playbooks. Please try again.',
          color: 'danger',
        });
      }
    },
    [selectedStrategy, selectedStrategyId, showToast, loadStrategies, loadPlaybooks]
  );

  /**
   * Move a playbook from source (current selected) strategy to target strategy.
   * Removes from source, adds to target, switches context to target strategy.
   */
  const movePlaybook = useCallback(
    async (playbookId: string, targetStrategyId: string) => {
      if (!selectedStrategy) return;

      const sourceStrategyId = selectedStrategy.id;

      try {
        // Fetch fresh strategies to avoid stale state
        const allStrategies = await strategyService.getAll();

        const sourceStrategy = allStrategies.find((s) => s.id === sourceStrategyId);
        const targetStrategy = allStrategies.find((s) => s.id === targetStrategyId);

        if (!sourceStrategy || !targetStrategy) {
          showToast({
            title: 'Move Failed',
            description: 'Strategy not found. It may have been deleted.',
            color: 'danger',
          });
          return;
        }

        // Get playbook for toast message
        const playbook = playbooks.find((p) => p.id === playbookId);
        const playbookName = playbook?.name || 'Playbook';

        // Remove from source strategy
        const sourcePlaybookIds = (sourceStrategy.playbookIds || []).filter(
          (id) => id !== playbookId
        );

        // Add to target strategy (with dedupe protection)
        const targetPlaybookIds = targetStrategy.playbookIds || [];
        const updatedTargetIds = targetPlaybookIds.includes(playbookId)
          ? targetPlaybookIds
          : [...targetPlaybookIds, playbookId];

        // Update source strategy
        await strategyService.update(sourceStrategyId, {
          playbookIds: sourcePlaybookIds,
        });
        syncCoordinatorService.notifyLocalChange('strategies', sourceStrategyId);
        syncCoordinatorService.notifyChange('strategies', sourceStrategyId);

        // Update target strategy
        await strategyService.update(targetStrategyId, {
          playbookIds: updatedTargetIds,
        });
        syncCoordinatorService.notifyLocalChange('strategies', targetStrategyId);
        syncCoordinatorService.notifyChange('strategies', targetStrategyId);

        // Reload strategies
        await loadStrategies();

        // Switch to target strategy (this triggers playbooks reload via existing effect)
        setSelectedStrategyId(targetStrategyId);

        // Preserve playbook selection (same ID, now in new strategy)
        setSelectedPlaybookId(playbookId);

        showToast({
          title: 'Playbook Moved',
          description: `"${playbookName}" moved to "${targetStrategy.name}"`,
          color: 'success',
        });
      } catch (error) {
        console.error('Failed to move playbook:', error);
        showToast({
          title: 'Move Failed',
          description: 'Could not move playbook. Please try again.',
          color: 'danger',
        });
      }
    },
    [selectedStrategy, playbooks, showToast, loadStrategies]
  );

  /**
   * Copy a playbook to a target strategy.
   * Creates a new playbook with cloned data and a unique name.
   * If copying to a different strategy, switches context to target strategy.
   */
  const copyPlaybook = useCallback(
    async (playbookId: string, targetStrategyId: string) => {
      try {
        // Fetch original playbook
        const originalPlaybook = await playbookService.get(playbookId);
        if (!originalPlaybook) {
          showToast({
            title: 'Copy Failed',
            description: 'Playbook not found. It may have been deleted.',
            color: 'danger',
          });
          return;
        }

        // Fetch target strategy's playbooks to get existing names for collision check
        const targetPlaybooks = await playbookService.getByStrategyId(targetStrategyId);
        const existingNames = targetPlaybooks.map((p) => p.name);

        // Generate unique copy name
        const newName = generateCopyName(originalPlaybook.name, existingNames);

        // Create new playbook with cloned data (status always 'active')
        // Only include optional fields if they have values (exactOptionalPropertyTypes)
        const newPlaybook = await playbookService.add({
          name: newName,
          ...(originalPlaybook.description !== undefined && { description: originalPlaybook.description }),
          ...(originalPlaybook.philosophy !== undefined && { philosophy: originalPlaybook.philosophy }),
          criteriaIds: originalPlaybook.criteriaIds ? [...originalPlaybook.criteriaIds] : [],
          confluences: originalPlaybook.confluences
            ? originalPlaybook.confluences.map((c) => ({ ...c }))
            : [],
          entryModelIds: originalPlaybook.entryModelIds ? [...originalPlaybook.entryModelIds] : [],
          status: 'active',
        });

        // Sync notification for new playbook
        syncCoordinatorService.notifyLocalChange('playbooks', newPlaybook.id);
        syncCoordinatorService.notifyChange('playbooks', newPlaybook.id);

        // Fetch fresh target strategy and add new playbook to its playbookIds
        const targetStrategy = await strategyService.get(targetStrategyId);
        if (!targetStrategy) {
          showToast({
            title: 'Copy Failed',
            description: 'Target strategy not found. It may have been deleted.',
            color: 'danger',
          });
          return;
        }

        const updatedPlaybookIds = [...(targetStrategy.playbookIds || []), newPlaybook.id];
        await strategyService.update(targetStrategyId, {
          playbookIds: updatedPlaybookIds,
        });

        // Sync notification for strategy
        syncCoordinatorService.notifyLocalChange('strategies', targetStrategyId);
        syncCoordinatorService.notifyChange('strategies', targetStrategyId);

        // Reload strategies
        await loadStrategies();

        // Context switch sequencing: set strategy first, then playbook (React batching)
        if (targetStrategyId !== selectedStrategyId) {
          setSelectedStrategyId(targetStrategyId);
        } else {
          // Same strategy - reload playbooks manually since strategy didn't change
          await loadPlaybooks(targetStrategyId);
        }
        setSelectedPlaybookId(newPlaybook.id);

        showToast({
          title: 'Playbook Copied',
          description: `"${newName}" created in "${targetStrategy.name}"`,
          color: 'success',
        });
      } catch (error) {
        console.error('Failed to copy playbook:', error);
        showToast({
          title: 'Copy Failed',
          description: 'Could not copy playbook. Please try again.',
          color: 'danger',
        });
      }
    },
    [selectedStrategyId, showToast, loadStrategies, loadPlaybooks]
  );

  /**
   * Share a playbook with one or more target strategies.
   * Adds the playbook ID to each target strategy's playbookIds[].
   * Playbook remains in current strategy (no context switch).
   */
  const sharePlaybook = useCallback(
    async (playbookId: string, targetStrategyIds: string[]) => {
      // Early return if no targets
      if (targetStrategyIds.length === 0) return;

      // Dedupe target IDs
      const uniqueTargetIds = Array.from(new Set(targetStrategyIds));

      try {
        // Get playbook for toast message
        const playbook = playbooks.find((p) => p.id === playbookId);
        const playbookName = playbook?.name || 'Playbook';

        // Process each target strategy
        let successCount = 0;
        for (const targetStrategyId of uniqueTargetIds) {
          // Fetch fresh strategy to avoid stale state
          const targetStrategy = await strategyService.get(targetStrategyId);
          if (!targetStrategy) {
            console.warn(`Strategy ${targetStrategyId} not found, skipping`);
            continue;
          }

          // Add playbookId to playbookIds[] (with dedupe protection)
          const existingIds = targetStrategy.playbookIds || [];
          if (existingIds.includes(playbookId)) {
            // Already contains this playbook, skip
            continue;
          }

          const updatedIds = [...existingIds, playbookId];
          await strategyService.update(targetStrategyId, {
            playbookIds: updatedIds,
          });

          // Sync notifications
          syncCoordinatorService.notifyLocalChange('strategies', targetStrategyId);
          syncCoordinatorService.notifyChange('strategies', targetStrategyId);

          successCount++;
        }

        // Reload strategies to update SharedIndicator counts
        await loadStrategies();

        // Show toast with count
        if (successCount > 0) {
          showToast({
            title: 'Playbook Shared',
            description: `"${playbookName}" shared with ${successCount} ${successCount === 1 ? 'strategy' : 'strategies'}`,
            color: 'success',
          });
        } else {
          // All selected strategies already contain the playbook
          showToast({
            title: 'Already Shared',
            description: `"${playbookName}" is already in selected strategies`,
            color: 'default',
          });
        }
      } catch (error) {
        console.error('Failed to share playbook:', error);
        showToast({
          title: 'Share Failed',
          description: 'Could not share playbook. Please try again.',
          color: 'danger',
        });
      }
    },
    [playbooks, showToast, loadStrategies]
  );

  /**
   * Optimistically add a newly created playbook to the list.
   * De-dupes by id in case sync subscription fires before/during insert.
   */
  const addPlaybookToList = useCallback((playbook: Playbook) => {
    setPlaybooks((prev) =>
      prev.some((p) => p.id === playbook.id) ? prev : [...prev, playbook]
    );
  }, []);

  // ===========================================================================
  // Context Value (Memoized)
  // ===========================================================================

  const value = useMemo<StrategiesPlaybooksContextValue>(
    () => ({
      // Hydration state
      isHydrating,

      // Strategy data
      strategies,
      selectedStrategy,
      selectedStrategyId,
      filteredStrategies,
      activeStrategies,
      archivedStrategies,
      loading,
      loadError,
      showArchived,

      // Strategy dirty state
      localDescription,
      localPhilosophy,
      isDirty,
      isSaving,
      showConflictBanner,

      // Strategy actions
      selectStrategy,
      setShowArchived,
      setLocalDescription,
      setLocalPhilosophy,
      saveStrategy,
      discardStrategyChanges,
      archiveStrategy,
      restoreStrategy,
      acceptExternalChanges,
      keepEditing,
      reloadStrategies,
      addStrategyToList,
      updateSelectedStrategyPlaybookIds,

      // Playbook data
      playbooks,
      selectedPlaybook,
      selectedPlaybookId,
      filteredPlaybooks,
      playbooksLoading,
      playbooksLoadError,
      showArchivedPlaybooks,
      playbooksForStrategyId: playbooksForStrategyIdRef.current,

      // Playbook dirty state
      localPlaybookDescription,
      localPlaybookPhilosophy,
      localPlaybookCriteriaIds,
      localPlaybookConfluences,
      localPlaybookEntryModelIds,
      isPlaybookDirty,
      isPlaybookSaving,
      showPlaybookConflictBanner,

      // Playbook actions
      selectPlaybook,
      setShowArchivedPlaybooks,
      setLocalPlaybookDescription,
      setLocalPlaybookPhilosophy,
      setLocalPlaybookCriteriaIds,
      setLocalPlaybookConfluences,
      setLocalPlaybookEntryModelIds,
      savePlaybook,
      discardPlaybookChanges,
      restorePlaybook,
      unlinkPlaybook,
      archivePlaybookFromList,
      restorePlaybookFromList,
      acceptPlaybookExternalChanges,
      keepPlaybookEditing,
      reloadPlaybooks,
      linkPlaybooks,
      movePlaybook,
      copyPlaybook,
      sharePlaybook,
      addPlaybookToList,

      // Linkage tab state
      activeLinkageTab,
      setActiveLinkageTab,

      // Playbook details collapse state
      isPlaybookDetailsExpanded,
      setIsPlaybookDetailsExpanded,
    }),
    [
      // Hydration state
      isHydrating,

      // Strategy data
      strategies,
      selectedStrategy,
      selectedStrategyId,
      filteredStrategies,
      activeStrategies,
      archivedStrategies,
      loading,
      loadError,
      showArchived,

      // Strategy dirty state
      localDescription,
      localPhilosophy,
      isDirty,
      isSaving,
      showConflictBanner,

      // Strategy actions
      selectStrategy,
      setShowArchived,
      saveStrategy,
      discardStrategyChanges,
      archiveStrategy,
      restoreStrategy,
      acceptExternalChanges,
      keepEditing,
      reloadStrategies,
      addStrategyToList,
      updateSelectedStrategyPlaybookIds,

      // Playbook data
      playbooks,
      selectedPlaybook,
      selectedPlaybookId,
      filteredPlaybooks,
      playbooksLoading,
      playbooksLoadError,
      showArchivedPlaybooks,

      // Playbook dirty state
      localPlaybookDescription,
      localPlaybookPhilosophy,
      localPlaybookCriteriaIds,
      localPlaybookConfluences,
      localPlaybookEntryModelIds,
      isPlaybookDirty,
      isPlaybookSaving,
      showPlaybookConflictBanner,

      // Playbook actions
      selectPlaybook,
      setShowArchivedPlaybooks,
      savePlaybook,
      discardPlaybookChanges,
      restorePlaybook,
      unlinkPlaybook,
      archivePlaybookFromList,
      restorePlaybookFromList,
      acceptPlaybookExternalChanges,
      keepPlaybookEditing,
      reloadPlaybooks,
      linkPlaybooks,
      movePlaybook,
      copyPlaybook,
      sharePlaybook,
      addPlaybookToList,

      // Linkage tab state
      activeLinkageTab,
      setActiveLinkageTab,

      // Playbook details collapse state
      isPlaybookDetailsExpanded,
      setIsPlaybookDetailsExpanded,
    ]
  );

  return (
    <StrategiesPlaybooksContext.Provider value={value}>
      {children}
    </StrategiesPlaybooksContext.Provider>
  );
};

// =============================================================================
// Consumer Hook
// =============================================================================

/**
 * Hook to consume the StrategiesPlaybooksContext.
 * Must be used within a StrategiesPlaybooksProvider.
 *
 * @throws Error if used outside of StrategiesPlaybooksProvider
 */
export function useStrategiesPlaybooks(): StrategiesPlaybooksContextValue {
  const context = useContext(StrategiesPlaybooksContext);
  if (!context) {
    throw new Error(
      'useStrategiesPlaybooks must be used within a StrategiesPlaybooksProvider. ' +
        'Wrap your component tree with <StrategiesPlaybooksProvider>.'
    );
  }
  return context;
}
